import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { getCurrency } from '@common/utils/currency.ts'
import { type ColumnDef } from '@tanstack/react-table'
import clsx from 'clsx'
import { useUnit } from 'effector-react'
import { useQueryClient } from 'react-query'
import { Link, unstable_usePrompt } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'

import PeopleListIcon from '@/assets/icons/icon-people-list.svg'
import ViewMoreIcon from '@/assets/icons/icon-view-more.svg'
import VisibilityOffIcon from '@/assets/icons/icon-visibility-off.svg'
import VisibilityOnIcon from '@/assets/icons/icon-visibility-on.svg'
import { Toggle } from '@/components/ui/Toggle.tsx'
import { Button, buttonVariants } from '@/components/ui/button'
import { CopyTextButton } from '@/components/ui/copy-text-button'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { $creatorId } from '@/models/creators.ts'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { TrackingLinksOverviewTableSkeleton } from '@/modules/tracking/links/components/tracking-links-overview-table-skeleton.tsx'
import type { ListLinkTrackingResponse } from '@/modules/tracking/links/data/service.ts'
import { useUpdateLinkTrackingVisibility } from '@/modules/tracking/links/query/link-tracking-id-query.tsx'
import { AppRoutes } from '@/utils/constants'
import { handleError } from '@/utils/error'
import { exportDataFromServer } from '@/utils/export/export'
import {
  debounce,
  formatNumberWithCommas,
  getPercentValue,
} from '@/utils/helpers'
import { usePagination } from '@/utils/pagination/pagination-context.tsx'
import { formatDate } from '@/utils/time'

import { useLinkTracking } from '../query/link-tracking-query'
import { type LinkType, type TrackingLink } from '../types'
import { CreateFanListDialog } from './create-fan-list-dialog'
import { ListCreationProgressDialog } from './list-creation-progress'

interface TrackingLinksTableProps {
  type: LinkType
}

interface MappedTrackingLink extends TrackingLink {
  inspectLink: string
  createdAtFormatted: string
}

interface CreateFanListModalState {
  linkName: string
  linkId: number
  isOpen: boolean
}

export const TrackingLinksOverviewTable = ({
  type,
}: TrackingLinksTableProps) => {
  const [showHidden, setShowHidden] = useState<boolean>(false)
  const [createFanListProgressModal, setCreateFanListProgressModal] = useState({
    isOpen: false,
    listId: '',
    listName: '',
  })
  const [createFanListModal, setCreateFanListModal] =
    useState<CreateFanListModalState>({
      linkName: '',
      linkId: 0,
      isOpen: false,
    })
  const { data, isFetching } = useLinkTracking({
    type,
    onlyVisible: !showHidden,
  })
  const updateVisibilityMutation = useUpdateLinkTrackingVisibility()
  const creatorId = useUnit($creatorId)
  const { pagination, setPagination } = usePagination()
  const queryClient = useQueryClient()
  const { userSettings } = useAccount()
  const shouldShowCents = useMemo(
    () => userSettings?.showCents,
    [userSettings?.showCents],
  )
  const [abortController, setAbortController] =
    useState<AbortController | null>(null)

  unstable_usePrompt({
    when: abortController !== null,
    message: 'You have active downloading. Are you sure you want to leave?',
  })

  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort()
      }
    }
  }, [abortController])

  const isTrackingType = type === 'Tracking'
  const contentWithInspectLink = useMemo(() => {
    return data?.content?.map((link) => ({
      ...link,
      inspectLink: isTrackingType
        ? AppRoutes.Creator.ChatterTracking.Links.Details.asUrl(
            creatorId || -1,
            link.linkId.toString(),
          )
        : AppRoutes.Creator.ChatterTracking.FreeTrialLinks.Details.asUrl(
            creatorId || -1,
            link.linkId.toString(),
          ),
      createdAtFormatted: formatDate(new Date(link.createdAt)),
    }))
  }, [creatorId, data, isTrackingType])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateVisibility = useCallback(
    debounce((linkId: number, visible: boolean) => {
      updateVisibilityMutation.mutateAsync(
        {
          linkId,
          type,
          visible,
        },
        {
          onSuccess: () => {
            queryClient.setQueryData<ListLinkTrackingResponse | undefined>(
              ['linkTracking', creatorId, type, !showHidden, pagination],
              (oldData) => {
                if (!oldData) {
                  return undefined
                }

                const newContent = showHidden
                  ? oldData?.content?.map((link) => {
                      if (link.linkId === linkId) {
                        return {
                          ...link,
                          visible,
                        }
                      }

                      return link
                    })
                  : oldData?.content?.filter((link) => link.linkId !== linkId)

                return {
                  ...oldData,
                  content: newContent,
                }
              },
            )
          },
          onError: (error) => {
            handleError(error, 'Failed to update link tracking visibility')
          },
        },
      )
    }, 800),
    [updateVisibilityMutation],
  )

  const handleCloseCreateFanListModal = () => {
    setCreateFanListModal((prev) => ({ ...prev, isOpen: false }))
  }

  const handleCreateFansList = (listId: string, listName: string) => {
    setCreateFanListProgressModal({ listId, listName, isOpen: true })
  }

  const columns: ColumnDef<MappedTrackingLink>[] = buildColumns(
    type,
    updateVisibility,
    shouldShowCents,
    setCreateFanListModal,
  )

  if (isFetching) {
    return <TrackingLinksOverviewTableSkeleton />
  }
  const downloadSheet = async () => {
    if (!creatorId) return

    if (abortController) {
      abortController.abort()
    }

    const newController = new AbortController()

    setAbortController(newController)

    try {
      const result = await exportDataFromServer(
        creatorId,
        {
          type: 'Links',
          filename: `${type} Links`,
          fields: {
            type,
          },
        },
        newController.signal,
      )

      setAbortController(null)

      return result
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <>
      <DataTable
        title={type === 'Tracking' ? 'Tracking Links' : 'Free Trial Links'}
        titleClassName="text-3xl"
        wrapperClassName="sm:[&_.actions-wrapper]:order-1 [&_.actions-wrapper]:order-0"
        searchColumn="name"
        columns={columns}
        data={contentWithInspectLink || []}
        // className="px-6 py-0"
        enableSearch
        enablePagination
        enableSorting
        paginationState={pagination}
        onPaginationChange={setPagination}
        pageCount={data?.totalPages || 0}
        enableExport
        tbodyMinHeight={false}
        exportOptions={{ getFromServer: downloadSheet }}
        extra={
          <div className="flex items-center gap-2">
            <label
              htmlFor="showHidden"
              className="text-text-default text-sm font-medium"
            >
              Show Hidden
            </label>
            <Toggle
              name="showHidden"
              checked={showHidden}
              onChange={(value) => {
                setShowHidden(value.target.checked)
              }}
            />
          </div>
        }
      />
      <CreateFanListDialog
        onClose={handleCloseCreateFanListModal}
        onCreate={handleCreateFansList}
        {...createFanListModal}
      />
      <ListCreationProgressDialog
        onClose={() =>
          setCreateFanListProgressModal((p) => ({ ...p, isOpen: false }))
        }
        {...createFanListProgressModal}
      />
    </>
  )
}

function buildColumns(
  type: LinkType,
  updateVisibility: (linkId: number, visible: boolean) => void,
  showCents = false,
  setIsCreateFanListModalOpen: Dispatch<
    SetStateAction<CreateFanListModalState>
  >,
): ColumnDef<MappedTrackingLink>[] {
  const columns: (ColumnDef<MappedTrackingLink> | undefined)[] = [
    {
      header: 'Link',
      accessorKey: 'name',
      enableSorting: true,
      sortingFn: 'text',
      cell: ({ getValue, row }) => (
        <div>
          <div className="flex items-center gap-2.5">
            <div className="font-bold">{getValue() as string}</div>
            <CopyTextButton
              text={row.original.url}
              toastMessage="Link copied to clipboard"
            />
          </div>
          <div className="text-secondary-300 text-sm">
            Created: {row.original.createdAtFormatted}
          </div>
        </div>
      ),
    },
    type === 'Tracking'
      ? {
          header: 'Link Clicks',
          accessorKey: 'linkClicks',
          enableSorting: true,
          sortingFn: 'alphanumeric',
          cell: ({ getValue }) => (
            <div>{formatNumberWithCommas(getValue() as number)}</div>
          ),
        }
      : undefined,
    {
      header: 'Claimed Fans',
      accessorKey: 'claimedFans',
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const value = getValue() as number

        return (
          <div>
            <div>{value}</div>
            {type === 'Tracking' && (
              <div className="text-secondary-300 text-sm">
                {getPercentValue(row.original.claimedFansConversion / 100)}{' '}
                conversion
              </div>
            )}
          </div>
        )
      },
    },
    {
      header: 'Revenue',
      accessorKey: 'revenue',
      enableSorting: true,
      sortingFn: 'alphanumeric',
      cell: ({ getValue, row }) => {
        const value = getValue() as number

        return (
          <div>
            <div
              className={clsx(
                value > 0 ? 'text-success-default' : 'text-danger-default',
              )}
            >
              {getCurrency(value, showCents ? 2 : 0)}
            </div>
            <div className="text-secondary-300 text-sm">
              from {row.original.spendingFans} fans
            </div>
          </div>
        )
      },
    },
    {
      header: 'Spending Fans',
      accessorKey: 'percentageSpendingFans',
      enableSorting: true,
      sortingFn: 'alphanumeric',
      cell: ({ row, getValue }) => {
        return <div>{getPercentValue((getValue() as number) / 100, 2)}</div>
      },
    },
    {
      header: 'Average Fan Spend',
      accessorKey: 'averageFanSpending',
      enableSorting: true,
      sortingFn: 'alphanumeric',
      cell: ({ getValue }) => (
        <div>{getCurrency(getValue() as string, showCents ? 2 : 0)}</div>
      ),
    },
    {
      header: () => <span className="w-full text-right">Actions</span>,
      id: 'action',
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-2">
          <Tooltip content="Create OnlyFans List">
            <Button
              className="text-title"
              variant="ghost"
              onClick={() =>
                setIsCreateFanListModalOpen({
                  linkName: row.original.name,
                  linkId: row.original.id,
                  isOpen: true,
                })
              }
            >
              <PeopleListIcon className="h-6 w-6" />
            </Button>
          </Tooltip>
          <Link
            to={row.original.inspectLink}
            // onClick={handleViewMoreClick(row.original.inspectLink)}
            className={twMerge(
              buttonVariants({ variant: 'ghost' }),
              'whitespace-nowrap p-0',
            )}
          >
            <ViewMoreIcon className="fill-primary-500 h-6 w-6" />
          </Link>
          <Tooltip content={row.original.visible ? 'Hide' : 'Show'}>
            <Button
              className="text-title"
              variant="ghost"
              onClick={() => {
                updateVisibility(row.original.linkId, !row.original.visible)
              }}
            >
              {row.original.visible ? (
                <VisibilityOnIcon className="h-6 w-6" />
              ) : (
                <VisibilityOffIcon className="h-6 w-6" />
              )}
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ]

  return columns.filter(Boolean) as ColumnDef<MappedTrackingLink>[]
}
